import { processListingParams } from '@/api/helpers';
import {
  AccountAdminTask,
  ListAdminTasksRequest,
  ListAdminTasksResponse,
  UpdateAccountAdminTaskRequest,
} from '@/client/admin-tasks/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export const accountAdminTasksService = (client: AxiosInstance) => {
  const ACCOUNT_TASKS_BASE_URL = (accountId: string) =>
    `/accounts/${accountId}/todos`;

  const getAccountAdminTasks = async (
    params: ListAdminTasksRequest = {},
    accountId: string,
  ): Promise<ListAdminTasksResponse> => {
    try {
      const result = await client.get<
        ListAdminTasksResponse,
        AxiosResponse<ListAdminTasksResponse>
      >(ACCOUNT_TASKS_BASE_URL(accountId), {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createAccountAdminTask = async (
    request: AccountAdminTask,
    accountId: string,
  ): Promise<AccountAdminTask> => {
    try {
      const result = await client.post<
        AccountAdminTask,
        AxiosResponse<AccountAdminTask>
      >(ACCOUNT_TASKS_BASE_URL(accountId), request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccountAdminTask = async (
    taskId: string,
    accountId: string,
  ): Promise<AccountAdminTask> => {
    try {
      const result = await client.get<
        AccountAdminTask,
        AxiosResponse<AccountAdminTask>
      >(`${ACCOUNT_TASKS_BASE_URL(accountId)}/${taskId}`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateAccountAdminTask = async (
    request: UpdateAccountAdminTaskRequest,
    accountId: string,
  ): Promise<AccountAdminTask> => {
    try {
      const result = await client.patch<
        AccountAdminTask,
        AxiosResponse<AccountAdminTask>
      >(
        `${ACCOUNT_TASKS_BASE_URL(accountId)}/${request.id as string}`,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteAccountAdminTask = async (
    taskId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_TASKS_BASE_URL(accountId)}/${taskId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw e as AxiosError;
    }
  };

  return {
    getAccountAdminTasks,
    createAccountAdminTask,
    getAccountAdminTask,
    updateAccountAdminTask,
    deleteAccountAdminTask,
  };
};
